import { useEffect, useRef, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { MOBILE_VIEWPORT } from '../guided-toolbox/utils/breakpoints'

/*
 * Tracks the on-screen keyboard's overlap with the layout viewport.
 *
 * A virtual keyboard does not shrink the layout viewport: `dvh` follows browser
 * chrome (the collapsing URL bar), not interactive widgets. So a `100dvh` shell
 * keeps its full height when the keyboard opens, the browser scrolls the page to
 * reveal the focused input, and everything above it — top nav, greeting,
 * suggestion pills, the streaming response — ends up off-screen.
 *
 * `visualViewport` is the only thing that reports the actually-visible area, so
 * we measure it and let the shell pin itself to that instead. Browsers that DO
 * resize the layout viewport (`interactive-widget=resizes-content`) measure an
 * inset of ~0 here and keep their native behavior.
 */

// Below this, a visual-viewport change is browser chrome (URL bar, toolbars) or
// pinch-zoom slack rather than a keyboard — don't resize the shell for it.
const KEYBOARD_MIN_HEIGHT = 100

/*
 * The keyboard's size is measured against the tallest visual viewport we've seen,
 * NOT by subtracting from a layout metric. Deriving it as
 * `innerHeight - visualViewport.height (- offsetTop)` does not survive contact
 * with real browsers: on iOS those metrics track the keyboard themselves, so the
 * terms cancel to ~0, the result falls under the threshold, and the shell is
 * never resized at all — the page just scrolls around behind the keyboard. That
 * was the bug this replaces. Android happened to work because its offsetTop stays
 * 0 and its innerHeight doesn't move.
 *
 * innerHeight only ever seeds the baseline (a floor), so it can't cancel anything
 * out. And the baseline only gates *whether* the keyboard is open — the shell's
 * height and offset come from visualViewport directly — so a baseline that is off
 * by less than the threshold costs nothing.
 *
 * The baseline only grows while no text field is focused. Otherwise a cold entry
 * poisons it: the input autofocuses on mount, Android raises the keyboard straight
 * away, and the first measurement is already shortened — so the baseline learns
 * the keyboard-open height, the inset reads 0 forever, and the shell never pins
 * (the footer then sits behind the keyboard). `pinToViewport` covers that case
 * regardless; skipping the growth stops it arising in the first place.
 */
const measure = (viewport, baseline, textEntryFocused) => {
  const height = Math.round(viewport.height)
  const nextBaseline = textEntryFocused ? baseline : Math.max(baseline, height, window.innerHeight)
  const inset = Math.max(0, nextBaseline - height)

  return {
    baseline: nextBaseline,
    metrics: {
      viewportHeight: height,
      viewportOffsetTop: Math.round(viewport.offsetTop),
      keyboardInset: inset >= KEYBOARD_MIN_HEIGHT ? inset : 0,
    },
  }
}

// A focused text field means the keyboard is up even when the geometric inset
// measures 0 — because the browser shrank the layout viewport itself, or because
// the baseline never saw a keyboard-free viewport to compare against.
const isTextEntry = node => node?.tagName === 'TEXTAREA' || node?.tagName === 'INPUT'

/**
 * @param {string} mobileViewport media query (no `@media` prefix) for the viewports whose keyboard
 *   overlays the page. A focused text field below this width is trusted as a raised keyboard even
 *   when the geometric inset measures 0 — see `pinToViewport` below.
 */
const useKeyboardInset = (mobileViewport = MOBILE_VIEWPORT) => {
  const [metrics, setMetrics] = useState({
    viewportHeight: 0,
    viewportOffsetTop: 0,
    keyboardInset: 0,
  })
  const [textEntryFocused, setTextEntryFocused] = useState(false)
  const baselineRef = useRef(0)
  // Mirrored so the viewport listeners below read the live value without having to
  // re-subscribe on every focus change.
  const textEntryFocusedRef = useRef(false)
  textEntryFocusedRef.current = textEntryFocused
  const isMobile = useMediaQuery(mobileViewport)

  useEffect(() => {
    const handleFocusIn = e => setTextEntryFocused(isTextEntry(e.target))
    const handleFocusOut = () => setTextEntryFocused(false)

    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)
    setTextEntryFocused(isTextEntry(document.activeElement))

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
    }
  }, [])

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return undefined

    const update = () => {
      const { baseline, metrics: next } = measure(
        viewport,
        baselineRef.current,
        textEntryFocusedRef.current
      )
      baselineRef.current = baseline

      setMetrics(prev =>
        prev.viewportHeight === next.viewportHeight &&
        prev.viewportOffsetTop === next.viewportOffsetTop &&
        prev.keyboardInset === next.keyboardInset
          ? prev
          : next
      )

      // Safety net for a page that was already scrolled when the keyboard came
      // up. Once the shell is pinned the document has nothing left to scroll, so
      // this can't turn into a tug-of-war with the browser's reveal scroll.
      if (next.keyboardInset > 0 && window.scrollY !== 0) window.scrollTo(0, 0)
    }

    // Rotating changes what "no keyboard" is worth, so re-learn the baseline.
    const handleOrientationChange = () => {
      baselineRef.current = 0
      update()
    }

    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    window.addEventListener('orientationchange', handleOrientationChange)

    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [])

  /*
   * A focused text field on a mobile viewport is enough to pin, without waiting
   * for the inset to clear its threshold: measuring the keyboard is the fragile
   * part, and a wrong answer there leaves the input stranded behind it. Pinning to
   * visualViewport.height is a visual no-op when no keyboard is up — that height
   * IS the visible area — so trusting focus costs nothing and removes the
   * measurement from the decision that matters most.
   */
  const canMeasure = metrics.viewportHeight > 0

  return {
    ...metrics,
    pinToViewport: canMeasure && (metrics.keyboardInset > 0 || (isMobile && textEntryFocused)),
  }
}

export default useKeyboardInset
